import React, { useEffect, useState, memo } from 'react';
import { authFetch } from '../../Utilities/Api';
import CommentSection from './CommentSection';
import EditPostModal from './EditPostModal';
import { extractEmbeds, renderEmbed } from '../../Utilities/EmbedRenderer';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { FaHeart, FaRegHeart, FaCommentAlt, FaGlobeAmericas, FaUsers, FaLock } from 'react-icons/fa';
import './PostItem.css';

// ✅ UPDATED: PostImage with .url support & LOGS
const PostImage = memo(({ image }) => {
    // 1. Safe access for NSFW (handle string vs object)
    const isNsfw = typeof image === 'object' && image?.nsfw;
    const [revealed, setRevealed] = useState(!isNsfw);

    // 2. Helper to get the correct path string
    const getImagePath = (img) => {
        if (!img) return '';
        // If it's just a string (e.g. "uploads/file.png"), return it directly
        if (typeof img === 'string') return img;

        // ✅ FIX: Check for .url (S3) first, then .webp, then .original
        return img.url || img.webp || img.original || '';
    };

    // 3. Helper to ensure full URL (Backend Prefix)
    const getFullUrl = (path) => {
        if (!path) {
            console.warn("⚠️ [PostImage] Extracted path is empty.", image);
            return '';
        }

        if (path.startsWith('http')) return path; // Already full URL

        // Prepend Render Backend URL
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        const fullUrl = `https://talkear.onrender.com/${cleanPath}`;

        // Debug conversion
        // console.log(`🔗 [PostImage] Converting: ${path} ➡️ ${fullUrl}`);
        return fullUrl;
    };

    // Calculate final URL
    const rawPath = getImagePath(image);
    const finalUrl = getFullUrl(rawPath);

    return (
        <div className="post-image-container">
            <img
                src={finalUrl}
                alt="Post content"
                className={`post-image ${isNsfw && !revealed ? 'blurred' : ''}`}
                loading="lazy"
                onError={(e) => {
                    console.error("❌ [PostImage] Failed to load:", finalUrl);
                    e.target.style.display = 'none';
                }}
            />
            {isNsfw && !revealed && (
                <div className="nsfw-overlay">
                    <button className="reveal-btn" onClick={() => setRevealed(true)}>
                        Reveal NSFW Content
                    </button>
                </div>
            )}
        </div>
    );
});

// ✅ Main Component
const PostItem = ({ post: initialPost, onPostDeleted, showComments: allowComments = true }) => {
    const [post, setPost] = useState(initialPost);
    const [likes, setLikes] = useState(initialPost.likes?.length || 0);
    const [liked, setLiked] = useState(false);
    const [comments, setComments] = useState(initialPost.comments || []);
    const [expanded, setExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // This state controls the toggle. It defaults to FALSE (closed).
    const [isCommentsOpen, setIsCommentsOpen] = useState(false);

    const userId = localStorage.getItem('userId');
    const truncateLimit = 300;

    const user = post.author || post.user || {};
    const postOwnerId = user._id?.toString() || user.toString();
    const isOwner = postOwnerId === userId;

    useEffect(() => {
        setPost(initialPost);
        setLikes(initialPost.likes?.length || 0);
        setComments(initialPost.comments || []);

        const isLikedByUser = initialPost.likes?.some(l => {
            const likerId = l._id || l.user?._id || l.user || l;
            return likerId.toString() === userId;
        });
        setLiked(!!isLikedByUser);
    }, [initialPost, userId]);

    const toggleLike = async () => {
        if (!userId) return;

        const wasLiked = liked;
        setLiked(!wasLiked);
        setLikes(prev => wasLiked ? prev - 1 : prev + 1);

        try {
            const res = await authFetch(`/profile-posts/like/${post._id}`, { method: 'POST' });
            if (!res.ok) throw new Error();
        } catch (err) {
            setLiked(wasLiked);
            setLikes(prev => wasLiked ? prev + 1 : prev - 1);
            console.error('❌ Failed to update like', err);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        try {
            const res = await authFetch(`/profile-posts/soft-delete/${post._id}`, { method: 'DELETE' });
            if (res.ok) onPostDeleted(post._id);
        } catch (err) {
            console.error('❌ Delete failed', err);
        }
    };

    const handleUpdate = (updatedPost) => {
        setPost(updatedPost);
        setIsEditing(false);
    };

    const shouldTruncate = post.text && post.text.length > truncateLimit;
    const displayText = expanded || !shouldTruncate ? post.text : post.text.slice(0, truncateLimit) + '...';

    const visibilityMap = {
        'public': { icon: <FaGlobeAmericas />, label: 'Public' },
        'friends-only': { icon: <FaUsers />, label: 'Friends' },
        'private': { icon: <FaLock />, label: 'Private' }
    };
    const currentVisibility = visibilityMap[post.visibility] || visibilityMap['public'];
    const embeds = expanded ? extractEmbeds(post.text || '') : [];

    return (
        <div className="post-item">
            <div className="post-header">
                <div className="post-user-info">
                    <span className="post-username">
                        {user.nickname || user.username || 'Unknown User'}
                    </span>
                    <div className="post-meta">
                        <span className="visibility-hint" title={currentVisibility.label}>
                            {currentVisibility.icon}
                        </span>
                        <span className="meta-separator">&middot;</span>
                        <span className="timestamp">
                            {post.createdAt ? formatDistanceToNow(new Date(post.createdAt), { addSuffix: true }) : 'Just now'}
                        </span>
                    </div>
                </div>
            </div>

            {post.images?.length > 0 && (
                <div className={`image-grid grid-${Math.min(post.images.length, 4)}`}>
                    {post.images.map((img, idx) => {
                        // 🔍 LOG: Inspect the data for each image
                        console.log(`🖼️ [PostItem] Rendering Image ${idx}:`, img);
                        return <PostImage key={idx} image={img} />;
                    })}
                </div>
            )}

            <div className="post-body">
                {post.text ? (
                    <>
                        <div className={`post-text-content ${!expanded && shouldTruncate ? 'truncated' : ''}`}>
                            <ReactMarkdown
                                children={displayText}
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeSanitize]}
                                components={{
                                    a: ({ node, ...props }) => (
                                        <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>
                                    )
                                }}
                            />
                        </div>
                        {expanded && embeds.length > 0 && (
                            <div className="post-embeds">
                                {embeds.map((url, i) => <div key={i}>{renderEmbed(url)}</div>)}
                            </div>
                        )}
                        {shouldTruncate && (
                            <button className="read-more-toggle" onClick={() => setExpanded(!expanded)}>
                                {expanded ? 'Show Less' : 'Read More'}
                            </button>
                        )}
                    </>
                ) : (
                    <p className="no-content">Shared a moment</p>
                )}
            </div>

            <div className="post-actions">
                <button
                    className={`action-btn like-btn ${liked ? 'liked' : ''}`}
                    onClick={toggleLike}
                    disabled={!userId}
                >
                    {liked ? <FaHeart /> : <FaRegHeart />}
                    <span>{likes}</span>
                </button>

                {/* The Toggle Button: Clicking this flips 'isCommentsOpen' */}
                <button
                    className={`action-btn comment-btn ${isCommentsOpen ? 'active' : ''}`}
                    onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                >
                    <FaCommentAlt />
                    <span>{comments.length}</span>
                </button>

                {isOwner && (
                    <div className="owner-controls">
                        <button className="control-btn edit" onClick={() => setIsEditing(true)}>Edit</button>
                        <button className="control-btn delete" onClick={handleDelete}>Delete</button>
                    </div>
                )}
            </div>

            {/* The Logic: Only show if allowed by parent AND opened by user */}
            {allowComments && isCommentsOpen && (
                <CommentSection
                    postId={post._id}
                    comments={comments}
                    setComments={setComments}
                    postVisibility={post.visibility}
                />
            )}

            {isEditing && (
                <EditPostModal
                    post={post}
                    onPostUpdated={handleUpdate}
                    onClose={() => setIsEditing(false)}
                />
            )}
        </div>
    );
};

export default PostItem;